import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BarChart3, Send, Loader2, Copy, Download, Printer, Check, Sparkles } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { sendAssistantMessage } from '../redux/assistantSlice';

// AI-Report & Analytics — ChatGPT-like page for doctor / admin / super-admin.
// Ask in plain language; the agent runs the report tools; this page renders the
// short insight text PLUS the structured results as tables + charts with
// CSV download / copy / print (save as PDF).

const SUGGESTIONS = [
    'Revenue this month, grouped by week',
    'Compare revenue this month vs last month',
    'Payments received vs outstanding this month',
    'Cancellation and no-show rates this quarter',
    'Who are my most active patients this month?',
    'List my patients with their last appointment',
];

function toCSV(columns, rows) {
    const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [columns.join(','), ...rows.map((r) => columns.map((c) => esc(r[c])).join(','))].join('\n');
}

function ReportCard({ report }) {
    const [copied, setCopied] = useState(false);
    const columns = report.columns || [];
    const rows = report.rows || [];
    const chart = report.chart;
    const summary = report.summary || {};

    const downloadCSV = () => {
        const blob = new Blob([toCSV(columns, rows)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(report.title || 'report').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyReport = async () => {
        const text = `${report.title}\n\n${columns.join('\t')}\n${rows.map((r) => columns.map((c) => r[c]).join('\t')).join('\n')}`;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) { /* clipboard blocked */ }
    };

    return (
        <div className="mt-3 rounded-2xl border border-[#4ABDBD]/25 bg-white p-4 shadow-sm" data-testid="report-card">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <h4 className="text-sm font-bold text-[#2D8A8A]">{report.title}</h4>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={copyReport} title="Copy" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button type="button" onClick={downloadCSV} title="Download CSV" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
                        <Download className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Summary chips */}
            {Object.keys(summary).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {Object.entries(summary).map(([k, v]) => (
                        <span key={k} className="px-2.5 py-1 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-xs font-semibold">
                            {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}: {typeof v === 'number' ? v.toLocaleString() : v}
                        </span>
                    ))}
                </div>
            )}

            {/* Chart */}
            {chart && rows.length > 0 && (
                <div className="h-56 mb-3">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey={chart.x} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} width={48} />
                            <Tooltip />
                            <Bar dataKey={chart.y} fill="#4ABDBD" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Table */}
            {rows.length > 0 ? (
                <div className="overflow-x-auto custom-scrollbar rounded-xl border border-gray-100">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((c) => (
                                    <th key={c} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">{c.replace(/_/g, ' ')}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                    {columns.map((c) => (
                                        <td key={c} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">
                                            {typeof r[c] === 'number' ? r[c].toLocaleString() : r[c]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-sm text-gray-400">No data for this period.</p>
            )}
        </div>
    );
}

export default function AIReports() {
    const { t } = useTranslation(['assistant']);
    const dispatch = useDispatch();
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [turns, setTurns] = useState([]); // { from:'user'|'bot', text, reports? }
    const sessionIdRef = useRef(`r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [turns]);

    const ask = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || busy) return;
        setInput('');
        setTurns((m) => [...m, { from: 'user', text }]);
        setBusy(true);
        try {
            const res = await dispatch(sendAssistantMessage({ message: text, sessionId: sessionIdRef.current, surface: 'reports' })).unwrap();
            setTurns((m) => [...m, { from: 'bot', text: res?.reply || '…', reports: res?.reports || [] }]);
        } catch (e) {
            setTurns((m) => [...m, { from: 'bot', text: t('assistant.error', 'Sorry — I could not answer right now. Please try again.'), reports: [] }]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto flex flex-col h-full" data-testid="ai-reports-page">
            {/* Header */}
            <div className="flex items-center justify-between py-4 px-1">
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] text-white">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-[#2D8A8A]">{t('assistant.reports_title', 'AI Reports & Analytics')}</h1>
                        <p className="text-xs text-gray-400">{t('assistant.reports_sub', 'Ask for revenue, payments, appointment stats or patient activity in plain language.')}</p>
                    </div>
                </div>
                <button type="button" onClick={() => window.print()} title={t('assistant.print', 'Print / save as PDF')} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
                    <Printer className="w-5 h-5" />
                </button>
            </div>

            {/* Conversation */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-1 pb-4" data-testid="ai-reports-conversation">
                {turns.length === 0 && (
                    <div className="text-center mt-14">
                        <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#4ABDBD]/60" />
                        <p className="text-sm text-gray-400 mb-5">{t('assistant.reports_hint', 'Try one of these:')}</p>
                        <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                            {SUGGESTIONS.map((s) => (
                                <button key={s} type="button" onClick={() => ask(s)} className="px-3 py-1.5 rounded-full border border-[#4ABDBD]/30 text-[#2D8A8A] text-xs hover:bg-[#4ABDBD]/10">
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {turns.map((m, i) => (
                    <div key={i} className={`mt-4 flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`${m.from === 'user' ? 'max-w-[75%] bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-2xl rounded-br-sm px-4 py-2 text-sm' : 'w-full'}`}>
                            {m.from === 'user' ? m.text : (
                                <div>
                                    <p className="text-sm text-gray-700 whitespace-pre-line">{m.text}</p>
                                    {(m.reports || []).map((r, j) => <ReportCard key={j} report={r} />)}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin text-[#3AA8A8]" /> {t('assistant.working', 'Crunching the numbers…')}
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="border-t border-gray-100 bg-white rounded-2xl shadow-sm p-3 mb-2 flex items-end gap-2">
                <textarea
                    data-testid="ai-reports-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                    placeholder={t('assistant.reports_placeholder', 'e.g. Compare my revenue this month vs last month…')}
                    rows={2}
                    className="flex-1 resize-none px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                />
                <button
                    type="button"
                    data-testid="ai-reports-send"
                    onClick={() => ask()}
                    disabled={busy || !input.trim()}
                    className="p-3 rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] disabled:opacity-40"
                >
                    <Send className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
